import React, { useState } from 'react'
import { Settings, Save, TestTube, Mail, CheckCircle, AlertCircle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import emailjs from '@emailjs/browser'

interface EmailConfigSectionProps {
  settings: {
    emailServiceId: string
    emailPublicKey: string
    emailTemplateId: string
    emailWeeklyTemplateId: string
    emailJSId: string | null
  }
  onSettingsChange: (settings: any) => void
  onShowMessage: (type: 'success' | 'error' | 'info', message: string) => void
  onRefresh: () => void
  isAccordion?: boolean
}

export const EmailConfigSection: React.FC<EmailConfigSectionProps> = ({
  settings,
  onSettingsChange,
  onShowMessage,
  onRefresh,
  isAccordion = false
}) => {
  const [isTestingConnection, setIsTestingConnection] = useState(false)
  const [isSavingConfig, setIsSavingConfig] = useState(false)

  const handleConfigChange = (field: string, value: string) => {
    onSettingsChange((prev: any) => ({
      ...prev,
      [field]: value
    }))
  }

  const testEmailConnection = async () => {
    try {
      setIsTestingConnection(true)
      onShowMessage('info', 'EmailJS verbinding wordt getest...')

      // Basic validation
      if (!settings.emailServiceId || !settings.emailPublicKey || !settings.emailTemplateId) {
        onShowMessage('error', 'Vul alle verplichte velden in voordat je de verbinding test')
        return
      }

      // Validate format
      if (!settings.emailServiceId.startsWith('service_')) {
        onShowMessage('error', 'Service ID moet beginnen met "service_"')
        return
      }

      if (!settings.emailTemplateId.startsWith('template_')) {
        onShowMessage('error', 'Template ID moet beginnen met "template_"')
        return
      }

      // Initialize EmailJS with provided config
      emailjs.init(settings.emailPublicKey)

      // Test EmailJS connection by sending a minimal test
      const testParams = {
        to_name: 'Test',
        to_email: 'test@example.com',
        from_name: 'KVH Dashboard Test',
        subject: 'Connection Test',
        message: 'This is a connection test from KVH Dashboard',
        test_mode: true
      }

      // Try to validate the service and template (this will fail gracefully if config is wrong)
      try {
        // We don't actually send this email, just test if the service/template combo is valid
        await emailjs.send(
          settings.emailServiceId,
          settings.emailTemplateId,
          testParams
        )
        onShowMessage('success', '✅ EmailJS verbinding succesvol! Service en template zijn correct geconfigureerd.')
      } catch (emailError: any) {
        console.error('EmailJS test error:', emailError)
        
        // Analyze the error to give helpful feedback
        if (emailError.text?.includes('User ID')) {
          onShowMessage('error', '❌ Public Key is incorrect. Controleer je EmailJS Public Key.')
        } else if (emailError.text?.includes('Service')) {
          onShowMessage('error', '❌ Service ID is incorrect. Controleer je EmailJS Service ID.')
        } else if (emailError.text?.includes('Template')) {
          onShowMessage('error', '❌ Template ID is incorrect. Controleer je EmailJS Template ID.')
        } else if (emailError.status === 422) {
          onShowMessage('error', '❌ Template variabelen kloppen niet. Controleer je EmailJS template.')
        } else {
          onShowMessage('error', `❌ EmailJS verbinding mislukt: ${emailError.text || emailError.message || 'Onbekende fout'}`)
        }
      }
    } catch (error: any) {
      console.error('❌ Error testing connection:', error)
      onShowMessage('error', `Fout bij het testen van de verbinding: ${error.message}`)
    } finally {
      setIsTestingConnection(false)
    }
  }

  const saveEmailConfig = async () => {
    try {
      setIsSavingConfig(true)
      onShowMessage('info', 'EmailJS configuratie wordt opgeslagen...')

      let error
      if (settings.emailJSId) {
        // Update existing record using the stored UUID
        const result = await supabase
          .from('emailJS')
          .update({
            serviceID: settings.emailServiceId,
            PublicKey: settings.emailPublicKey,
            DailyTemplateID: settings.emailTemplateId,
            WeeklyTemplateID: settings.emailWeeklyTemplateId
          })
          .eq('id', settings.emailJSId)
        error = result.error
      } else {
        // Insert new record (let database generate UUID)
        const result = await supabase
          .from('emailJS')
          .insert({
            serviceID: settings.emailServiceId,
            PublicKey: settings.emailPublicKey,
            DailyTemplateID: settings.emailTemplateId,
            WeeklyTemplateID: settings.emailWeeklyTemplateId
          })
        error = result.error
      }

      if (error) {
        console.error('❌ Error saving email config:', error)
        onShowMessage('error', 'Fout bij het opslaan van EmailJS configuratie')
        return
      }

      onShowMessage('success', 'EmailJS configuratie opgeslagen!')
      onRefresh() // Refresh settings from parent
    } catch (error) {
      console.error('❌ Error saving email config:', error)
      onShowMessage('error', 'Fout bij het opslaan van EmailJS configuratie')
    } finally {
      setIsSavingConfig(false)
    }
  }

  return (
    <div className={isAccordion ? '' : 'bg-white rounded-lg shadow-md p-6 mb-6'}>
      {!isAccordion && (
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <Settings className="text-kvh-red" />
          EmailJS Configuratie
        </h2>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service ID *
          </label>
          <input
            type="text"
            value={settings.emailServiceId}
            onChange={(e) => handleConfigChange('emailServiceId', e.target.value)}
            placeholder="service_xxxxx"
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-kvh-red focus:border-kvh-red"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Public Key *
          </label>
          <input
            type="text"
            value={settings.emailPublicKey}
            onChange={(e) => handleConfigChange('emailPublicKey', e.target.value)}
            placeholder="xxxxxxxxx"
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-kvh-red focus:border-kvh-red"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Daily Template ID *
          </label>
          <input
            type="text"
            value={settings.emailTemplateId}
            onChange={(e) => handleConfigChange('emailTemplateId', e.target.value)}
            placeholder="template_xxxxx"
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-kvh-red focus:border-kvh-red"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Weekly Template ID
          </label>
          <input
            type="text"
            value={settings.emailWeeklyTemplateId}
            onChange={(e) => handleConfigChange('emailWeeklyTemplateId', e.target.value)}
            placeholder="template_xxxxx (optioneel)"
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-kvh-red focus:border-kvh-red"
          />
        </div>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-lg mb-4">
        <h3 className="font-semibold text-blue-800 mb-2">💡 EmailJS Setup Guide</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Ga naar <a href="https://emailjs.com" target="_blank" rel="noopener noreferrer" className="underline">emailjs.com</a> en maak een account</li>
          <li>• Creëer een nieuwe service (Gmail, Outlook, etc.)</li>
          <li>• Maak email templates voor dagelijkse en wekelijkse rapporten</li>
          <li>• Kopieer je Service ID, Public Key, en Template IDs hierboven</li>
        </ul>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          onClick={saveEmailConfig}
          disabled={isSavingConfig}
          className="px-4 py-2 min-h-touch bg-kvh-red text-white rounded hover:bg-kvh-dark-red transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSavingConfig ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save size={16} />
          )}
          {isSavingConfig ? 'Opslaan...' : 'EmailJS Config Opslaan'}
        </button>
        
        <button
          onClick={testEmailConnection}
          disabled={isTestingConnection}
          className="px-4 py-2 min-h-touch bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isTestingConnection ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <TestTube size={16} />
          )}
          {isTestingConnection ? 'Testen...' : 'Test Verbinding'}
        </button>
      </div>
    </div>
  )
}